import { Board } from "../board";
import { BOARD_WIDTH, BOARD_HEIGHT } from "../gameConfig";
import { randomChance } from "../random";
import { CaveRock } from "../things/caveRock";
import { Floor } from "../things/floors/floor";
import { MobSpawnerConfig } from "../things/portal2/mobSpawnerConfig";
import { RobotSpawner } from "../things/robotSpawner";
import { XY } from "../xy";
import { generateWithRetries } from "./levelGenerator";
import { populateLootBoxes, assignGoldToPrePlacedMobs } from "./levelGeneratorUtils";
import type { LootTable } from "./lootTable";
import { buildPrefabWithOverrides } from "./prefabs/prefabBuilder";
import { robotIntroLevelData } from "./prefabs/robotIntroLevelData";

export interface RobotIntroLevelParams {
	levelNumber: number;
	rng?: () => number;
	lootTable?: LootTable | null;
	metalLootTable?: LootTable | null;
	rewardLevel?: number;
}

export function generateRobotIntroLevel(params: RobotIntroLevelParams): Board {
	return generateWithRetries(params.levelNumber, () => runGeneration(params));
}

function runGeneration(p: RobotIntroLevelParams): Board {
	const rng = p.rng ?? Math.random;
	const board = new Board(BOARD_WIDTH, BOARD_HEIGHT, rng);
	board.defaultFloor = "CaveFloor";

	const prefab = buildPrefabWithOverrides(robotIntroLevelData, {
		D: { class: "Downstairs", props: { destination: p.levelNumber + 1 } },
		U: { class: "Upstairs", props: { destination: p.levelNumber - 1 } },
	});
	prefab.addToBoard(board, new XY(0, 2));

	// Randomize RobotSpawners: 50/50 Robot or FloatingRobot
	for (const thing of board.getPhysicalThings()) {
		if (thing instanceof RobotSpawner) {
			const config = new MobSpawnerConfig();
			config.addEntry({
				mobType: randomChance(0.5, rng) ? "Robot" : "FloatingRobot",
				share: 1,
			});
			thing.setMobSpawnerConfig(config);
		}
	}

	// Fill passable cells without a Floor with CaveRock
	for (const xy of board.getAllXYs()) {
		const hasFloor = board.getThingsAt(xy).some((t) => t instanceof Floor);
		if (!hasFloor && board.isPassable(xy)) {
			board.addThing(new CaveRock(xy));
		}
	}

	populateLootBoxes(board, p.lootTable ?? null, p.metalLootTable ?? null, p.rewardLevel ?? 0, rng);
	assignGoldToPrePlacedMobs(board);
	board.dynamicGoldBudget = 100;

	return board;
}
